import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import {EMPTY, of, tap} from 'rxjs';
import {switchMap, map, catchError, withLatestFrom, filter, concatMap} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {
  downloadCadModelRevisionDrawing,
  downloadCadModelRevisionDrawingFailure,
  downloadCadModelRevisionDrawingSuccess,
  loadCadModelRevisionDrawings,
  loadCadModelRevisionDrawingsFailure,
  loadCadModelRevisionDrawingsSuccess,
  loadCadModelDrawingBlob,
  loadCadModelDrawingBlobFailure,
  loadCadModelDrawingBlobSuccess,
  modifyCadModelRevisionDrawing,
  modifyCadModelRevisionDrawingFailure,
  modifyCadModelRevisionDrawingSuccess,
  removeBulkCadModelRevisionDrawing,
  removeBulkCadModelRevisionDrawingFailure,
  removeBulkCadModelRevisionDrawingSuccess,
  uploadCadModelRevisionDrawing,
  uploadCadModelRevisionDrawingFailure,
  uploadCadModelRevisionDrawingSuccess,
  toggleDrawingRedline,
  toggleDrawingRedlineSuccess,
  toggleDrawingRedlineFailure,
  toggleDrawingNonConformance,
  toggleDrawingNonConformanceSuccess,
  toggleDrawingNonConformanceFailure,
  saveDrawingScribble,
  saveDrawingScribbleSuccess,
  saveDrawingScribbleFailure,
} from "./cad-model-revision-drawing.action";

import {AppState} from "../../../shared/state/app.state";
import {CadModelService} from "../../data-access/cad-model.service";
import {Dictionary} from "@ngrx/entity";
import {CadModelRevisionDrawing} from "../../../shared/models/cad-model.model";
import {selectCadModelRevisionDrawingEntities, selectCadModelDrawingBlobMap, selectBuildKitDrawingsMap} from "./cad-model-revision-drawing.selectors";
import {ToastMessageOptions, MessageService} from "primeng/api";
import {GetMessage} from "../../../global-config/utils/toast-messages.utils";
import {DrawingService} from "../../../drawing/data-access/drawing.service";


@Injectable()
export class CadModelRevisionDrawingEffects {
  loadCadModelRevisionDrawings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCadModelRevisionDrawings),
      withLatestFrom(this.store.select(selectCadModelRevisionDrawingEntities)),
      switchMap(([action, drawings]: [any, Dictionary<CadModelRevisionDrawing>]) => {
        if (drawings[action.cadModelRevisionId]){
          return of(loadCadModelRevisionDrawingsSuccess({
            cadModelRevisionDrawing: drawings[action.cadModelRevisionId] as CadModelRevisionDrawing
          }));
        }
        return this.cadModelService.getCadModelRevisionWithDrawings(action.cadModelRevisionId)
          .pipe(
            map((response) => {
              return loadCadModelRevisionDrawingsSuccess({
                cadModelRevisionDrawing: { id: response.id, drawings: response.drawings },
              })
            }),
            catchError((response) => of(loadCadModelRevisionDrawingsFailure(
              {
                error: GetMessage('LoadFailure', 'Drawings')
              }
            )))
          )
        }
      )
    )
  );

  reLoadCadModelRevisionDrawings = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadCadModelRevisionDrawingSuccess, removeBulkCadModelRevisionDrawingSuccess, modifyCadModelRevisionDrawingSuccess),
      switchMap((action) => {
          return this.cadModelService.getCadModelRevisionWithDrawings(action.cadModelRevisionId)
            .pipe(
              map((response) => {
                return loadCadModelRevisionDrawingsSuccess({
                  cadModelRevisionDrawing: { id: response.id, drawings: response.drawings },
                })
              }),
              catchError(() => EMPTY)
            )
        }
      )
    )
  );

  downloadCadModelRevisionDrawing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(downloadCadModelRevisionDrawing),
      switchMap((action) => {
          return this.cadModelService.downloadDrawing(action.drawingId, action.fileName)
            .pipe(
              map((file) => downloadCadModelRevisionDrawingSuccess({file: file})),
              catchError((error) => of(downloadCadModelRevisionDrawingFailure({error})))
            )
        }
      )
    )
  );

  removeBulkCadModelRevisionDrawing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeBulkCadModelRevisionDrawing),
      switchMap((action) => {
          return this.cadModelService.removeBulkCadModelRevisionDrawings(action.drawingIds)
            .pipe(
              map((drawing) => removeBulkCadModelRevisionDrawingSuccess({
                cadModelRevisionId: action.cadModelRevisionId, successMessage: GetMessage('RemoveSuccess', 'Drawing')
              })),
              catchError((error) => of(removeBulkCadModelRevisionDrawingFailure({error: GetMessage('RemoveFailure', 'Drawing')})))
            )
        }
      )
    )
  );

  modifyCadModelRevisionDrawing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(modifyCadModelRevisionDrawing),
      switchMap((action) => {
          return this.cadModelService.modifyCadModelRevisionDrawing(action.drawing)
            .pipe(
              map((response) => modifyCadModelRevisionDrawingSuccess({
                drawing: response.data,
                cadModelRevisionId: response.data.cadModelRevisionId,
                successMessage: GetMessage('ModifySuccess', 'Drawing')
              })),
              catchError((error) => of(modifyCadModelRevisionDrawingFailure({error: GetMessage('ModifyFailure', 'Drawing')})))
            )
        }
      )
    )
  );

  uploadCadModelRevisionDrawing = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadCadModelRevisionDrawing),
      switchMap((action) => {
        return this.cadModelService.uploadDrawings(action.cadModelRevisionId, action.drawingFiles)
          .pipe(
            map((drawing) => uploadCadModelRevisionDrawingSuccess({cadModelRevisionId: action.cadModelRevisionId, drawing: drawing.data, successMessage: GetMessage('UploadSuccess', 'Drawing')})),
            catchError((error) => of(uploadCadModelRevisionDrawingFailure({error: GetMessage('UploadFailure', 'Drawing')})))
          );
        }
      )
    )
  );

  toastSuccessMessages$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          uploadCadModelRevisionDrawingSuccess,
          modifyCadModelRevisionDrawingSuccess,
          removeBulkCadModelRevisionDrawingSuccess,
        ),
        tap(({ successMessage }) => {
            return this.messageService.add({
              severity: 'success',
              summary: successMessage,
            } as ToastMessageOptions)
          }
        )
      ),
    { dispatch: false }
  );

  toastScribbleFailed$ = createEffect(() =>
      this.actions$.pipe(
        ofType(saveDrawingScribbleFailure),
        tap(({ error }) => this.messageService.add({ severity: 'error', summary: error } as ToastMessageOptions))
      ),
    { dispatch: false }
  );

  toastErrorMessages$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          uploadCadModelRevisionDrawingFailure,
          removeBulkCadModelRevisionDrawingFailure,
          modifyCadModelRevisionDrawingFailure,
          loadCadModelRevisionDrawingsFailure,
        ),
        tap(({ error }) => {
            return this.messageService.add({
              severity: 'error',
              summary: error,
            } as ToastMessageOptions)
          }
        )
      ),
    { dispatch: false }
  );

  toggleDrawingRedline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleDrawingRedline),
      switchMap(({ drawingId }) =>
        this.drawingService.toggleRedline(drawingId).pipe(
          map(response => toggleDrawingRedlineSuccess({ drawing: response.data })),
          catchError(() => of(toggleDrawingRedlineFailure({ error: 'Failed to toggle Redline' })))
        )
      )
    )
  );

  toggleDrawingNonConformance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleDrawingNonConformance),
      switchMap(({ drawingId }) =>
        this.drawingService.toggleNonConformance(drawingId).pipe(
          map(response => toggleDrawingNonConformanceSuccess({ drawing: response.data })),
          catchError(() => of(toggleDrawingNonConformanceFailure({ error: 'Failed to toggle NonConformance' })))
        )
      )
    )
  );

  saveDrawingScribble$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveDrawingScribble),
      switchMap(({ drawingId, pdfDrawingFileKey, blob }) =>
        this.drawingService.saveScribble(drawingId, blob).pipe(
          map(() => saveDrawingScribbleSuccess({ drawingId, pdfDrawingFileKey, blob })),
          catchError(() => of(saveDrawingScribbleFailure({ error: 'Failed to save Markup' })))
        )
      )
    )
  );

  enableRedlineOnScribbleSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveDrawingScribbleSuccess),
      withLatestFrom(
        this.store.select(selectBuildKitDrawingsMap),
        this.store.select(selectCadModelRevisionDrawingEntities)
      ),
      filter(([{ drawingId }, buildKitDrawingsMap, entities]) => {
        for (const kitId in buildKitDrawingsMap) {
          const d = buildKitDrawingsMap[kitId].find((d: any) => d.id === drawingId);
          if (d) return !(d.isRedline ?? false);
        }
        for (const key in entities) {
          const entity = entities[key];
          if (entity?.drawings) {
            const d = entity.drawings.find((d: any) => d.id === drawingId);
            if (d) return !(d.isRedline ?? false);
          }
        }
        return false;
      }),
      map(([{ drawingId }]) => toggleDrawingRedline({ drawingId }))
    )
  );

  loadCadModelDrawingBlob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCadModelDrawingBlob),
      withLatestFrom(this.store.select(selectCadModelDrawingBlobMap)),
      filter(([action, blobDrawingMap]) => {
        if (!(action.pdfDrawingFileKey in blobDrawingMap)){
          return true;
        }
        return blobDrawingMap[action.pdfDrawingFileKey].inProgress === true &&
               blobDrawingMap[action.pdfDrawingFileKey].previousCallInProgress === false &&
               blobDrawingMap[action.pdfDrawingFileKey].completed === false;
      }),
      concatMap(([action, blobDrawingMap]) =>
        this.drawingService.downloadByPdfDrawingKey(action.pdfDrawingFileKey)
          .pipe(
            map((blob) => loadCadModelDrawingBlobSuccess({pdfDrawingFileKey: action.pdfDrawingFileKey, blob })),
            catchError((error) => of(loadCadModelDrawingBlobFailure({ error })))
          )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private cadModelService: CadModelService,
    private drawingService: DrawingService,
    private messageService: MessageService,
  ) {}
}
